import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../App';

const LoginPage: React.FC = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const { isAuthenticated, isLoading } = useAppStore();
 const errorMessage = searchParams.get('error');

 // 관리자 사전생성 계정 인계 — 코드 입력 → /auth/claim → Google 로그인으로.
 const [claimOpen, setClaimOpen] = useState(false);
 const [claimCode, setClaimCode] = useState('');
 const [claimErr, setClaimErr] = useState<string | null>(null);
 const [claiming, setClaiming] = useState(false);

 const handleClaim = async () => {
   setClaimErr(null);
   setClaiming(true);
   try {
     const r = await fetch('/auth/claim', {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({ code: claimCode.trim() }),
     });
     const d = await r.json();
     if (r.ok && d.success && d.redirect) {
       window.location.href = d.redirect; // Google 로그인 → 콜백이 인계 처리
     } else {
       setClaimErr(d.error || '인계 코드 확인에 실패했습니다.');
       setClaiming(false);
     }
   } catch {
     setClaimErr('요청 중 오류가 발생했습니다.');
     setClaiming(false);
   }
 };

 useEffect(() => {
 if (!isLoading && isAuthenticated) {
 navigate('/dashboard');
 }
 }, [isAuthenticated, isLoading, navigate]);

 if (isLoading) {
 return (
 <div className="bg-gray-50 min-h-screen flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin h-12 w-12 rounded-full border-4 border-gray-200 border-t-blue-600 mx-auto mb-4"></div>
 <p className="text-gray-600">인증 확인 중...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="bg-gray-50 min-h-screen flex items-center justify-center">
 <div className="bg-white p-8 shadow-md w-96">
 <div className="flex items-center justify-center mb-6">
 <img src="/static/MOC-arrowEar.png" alt="MOcombinator" className="w-12 h-12 mr-3" />
 <h1 className="text-2xl font-bold text-gray-800">MOcombinator</h1>
 </div>
 <p className="text-gray-600 text-center mb-2"> 
 분산 네트워크 디지털 사이니지 플랫폼
 </p>
 <p className="text-gray-400 text-center text-sm mb-6">
 모패밀리의 구성원이 되어 광고를 함께 송출하세요.
 </p>

 <div className="bg-gray-50 p-4 mb-6 text-sm text-gray-500 leading-relaxed">
 <p className="mb-2">
 스크린에 표시할 콘텐츠를 등록하고, 재생 스케줄을 관리하세요.
 </p>
 <ul className="space-y-1 pl-4 list-disc">
 <li>그룹(모패밀리) 단위 스크린 관리</li>
 <li>분산 네트워크 기반 콘텐츠 자동 배포</li>
 <li>원격 재생 제어</li>
 </ul>
 </div>

 {errorMessage && (
 <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4 text-center">
 {errorMessage}
 </div>
 )}

 <a
 href="/auth/google"
 className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 text-center transition-colors"
 >
 Google로 로그인
 </a>

 <div className="mt-4 text-center">
 <button
 type="button"
 onClick={() => setClaimOpen((o) => !o)}
 className="text-sm text-gray-500 hover:text-blue-600 underline"
 >
 관리자에게 받은 인계 코드가 있으신가요?
 </button>
 </div>
 {claimOpen && (
 <div className="mt-3 border-t pt-4">
 <p className="text-xs text-gray-500 mb-2">
 관리자가 미리 만들어 둔 계정을 인계받습니다. 코드 입력 후 Google 로그인하세요.
 </p>
 <input
 value={claimCode}
 onChange={(e) => setClaimCode(e.target.value)}
 placeholder="인계 코드"
 className="w-full border border-gray-300 px-3 py-2 mb-2 text-sm uppercase tracking-wider"
 />
 {claimErr && <div className="text-red-600 text-xs mb-2">{claimErr}</div>}
 <button
 type="button"
 onClick={handleClaim}
 disabled={claiming || !claimCode.trim()}
 className="w-full bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white py-2 text-sm transition-colors"
 >
 {claiming ? '확인 중...' : 'Google로 인계받기'}
 </button>
 </div>
 )}

 <div className="mt-6 pt-4 border-t border-gray-100 text-center text-xs text-gray-400 space-x-3">
 <a href="/privacy.html" className="hover:text-blue-600 hover:underline">개인정보처리방침</a>
 <span>·</span>
 <a href="/terms.html" className="hover:text-blue-600 hover:underline">이용약관</a>
 </div>
 </div>
 </div>
 );
};

export default LoginPage;
